import type { Resultado } from './acesso';
import type { QualidadeDaEquipe } from './auditoria';
import type { Sessao } from './auth';
import { CABECALHO_CSRF } from './auth';
import { buscar } from './buscar';

/**
 * BLOCO 10 — o front de /api/giro (telas 16–21).
 *
 * Escrita tem TRÊS desfechos, nunca dois: confirmada, recusada (o servidor disse não, com o motivo)
 * e INCERTA (a rede caiu no meio — pode ter gravado). Incerto nunca é mostrado como recusa.
 */

export type Escrita<T> =
  | { readonly tipo: 'confirmada'; readonly dados: T }
  | { readonly tipo: 'recusada'; readonly mensagem: string; readonly status: number }
  | { readonly tipo: 'incerta' };

async function ler<T>(caminho: string): Promise<Resultado<T>> {
  let r: Response;
  try {
    r = await buscar(caminho, {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.ok) return { ok: true, dados: (await r.json()) as T };
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

async function escrever<T>(
  sessao: Sessao,
  metodo: 'POST' | 'PUT',
  caminho: string,
  corpo?: unknown,
): Promise<Escrita<T>> {
  let r: Response;
  try {
    r = await fetch(caminho, {
      method: metodo,
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [CABECALHO_CSRF]: sessao.csrfToken },
      body: JSON.stringify(corpo ?? {}),
    });
  } catch {
    return { tipo: 'incerta' };
  }
  if (r.ok) {
    const texto = await r.text();
    return { tipo: 'confirmada', dados: (texto ? JSON.parse(texto) : null) as T };
  }
  if (r.status >= 500) return { tipo: 'incerta' };
  let mensagem = '';
  try {
    mensagem = ((await r.json()) as { error?: { message?: string } }).error?.message ?? '';
  } catch {
    // sem corpo
  }
  return { tipo: 'recusada', mensagem: mensagem || 'Não foi aceito.', status: r.status };
}

/** Os mesmos três desfechos para a auditoria (Bloco 11). */
export { ler as lerDaApi, escrever as escreverNaApi };

/* ------------------------------------------------------------------ *
 * Tipos (espelham @inovapay/database/giro)
 * ------------------------------------------------------------------ */

export type MotivoForaDaMissao =
  | 'ESTOQUE_ZERO'
  | 'ESTOQUE_INDISPONIVEL'
  | 'ESTOQUE_STALE'
  | 'SEM_SETOR'
  | 'SEM_CADASTRO'
  | 'SETOR_SEM_EQUIPE';
export type EtapaDoBloco =
  | 'SEM_CARGA'
  | 'AGUARDANDO_RESPONSAVEL'
  | 'RASCUNHO'
  | 'NAO_INICIADO'
  | 'CONTAGEM'
  | 'REPOSICAO'
  | 'AUDITORIA_PENDENTE'
  | 'CONCLUIDO'
  | 'PARCIAL'
  | 'CANCELADO';

export interface BlocoDaOperacao {
  readonly numero: number;
  readonly rotulo: string;
  readonly total: number;
  readonly letraQuebrada: boolean;
  readonly missaoId: string | null;
  readonly responsavel: {
    readonly accountId: string;
    readonly nome: string;
    readonly presenca: string;
  } | null;
  readonly etapa: EtapaDoBloco;
  readonly contados: number;
  readonly aRepor: number;
  readonly repostos: number;
  readonly divergencias: number;
  readonly aguardandoEstoque: number;
  readonly fotos: number;
  readonly iniciadaEm: string | null;
  readonly contagemConcluidaEm: string | null;
  readonly concluidaEm: string | null;
  readonly transferencia: {
    readonly de: string;
    readonly deNome: string;
    readonly em: string;
    readonly contadosAntes: number;
    readonly motivo: string;
  } | null;
  readonly contadosPorResponsavel: number;
}
export interface SetorDaOperacao {
  readonly setorId: string;
  readonly nome: string;
  readonly descricao: string | null;
  readonly geracao: {
    readonly id: string;
    readonly estado: 'RASCUNHO' | 'PUBLICADA' | 'CANCELADA';
    readonly vendasDe: string;
    readonly criadoEm: string;
    readonly publicadoEm: string | null;
    readonly publicadoPor: string | null;
    readonly elegiveis: number;
    readonly cargaAlvo: number;
    readonly presentes: number;
    readonly motivoCancelamento: string | null;
    /** Montado com o último dia da captura lido pela metade: o aviso (null = completo). */
    readonly vendasIncompletas: string | null;
  } | null;
  readonly vinculados: number;
  readonly presentesHoje: number;
  readonly blocos: readonly BlocoDaOperacao[];
}
/** Até onde as vendas de um dia já foram lidas do HOS (D16: Giro não sai de meio dia de venda). */
export interface LeituraDasVendas {
  readonly dia: string;
  readonly lidasAte: string | null;
  readonly completa: boolean;
  readonly aviso: string | null;
}
export interface OperacaoDoDia {
  readonly dia: string;
  readonly vendasDe: string;
  /** Primeiro dia das vendas usadas: segunda com domingo sem Giro = sábado. */
  readonly vendasDesde: string;
  readonly vendasLidas: LeituraDasVendas;
  readonly vendidosD1: number | null;
  readonly elegiveis: number;
  readonly fora: Readonly<Record<MotivoForaDaMissao, number>>;
  readonly foraTotal: number;
  readonly setores: readonly SetorDaOperacao[];
  readonly progresso: {
    readonly contados: number;
    readonly totalItens: number;
    readonly repostos: number;
    readonly aRepor: number;
    readonly blocosConcluidos: number;
    readonly blocosEmAndamento: number;
    readonly blocosNaoIniciados: number;
    readonly divergencias: number;
    readonly auditoriaPendente: number;
  };
  readonly alertas: readonly {
    readonly tipo: string;
    readonly titulo: string;
    readonly detalhe: string;
    readonly acao: string | null;
    readonly setorId: string | null;
  }[];
  readonly estoqueCapturadoAte: string | null;
  /** Última leitura de estoque que chegou do HOS (mesmo sem geração no dia). */
  readonly estoqueLidoEm?: string | null;
  readonly atualizadoEm: string;
}
export interface ProdutoDoBloco {
  readonly itemId: string;
  readonly letra: string;
  readonly nome: string;
  readonly codigo: string | null;
  /** Fabricante/laboratório do cadastro (null = sem cadastro). */
  readonly fabricante: string | null;
  readonly prateleira: number | null;
  readonly naoLocalizado: boolean;
  readonly alvo: number;
  readonly qtdVendida: number;
  readonly mediaDiaria: number | null;
  readonly origem: 'VENDIDO' | 'NAO_VENDIDO' | null;
  readonly estoque: number | null;
  readonly estoqueTexto: string;
  readonly repor: number | null;
  readonly encontrado: number | null;
  readonly reposto: number | null;
  readonly estado: string;
  readonly autor: string | null;
}
export interface DetalheDoBloco {
  readonly geracaoId: string;
  readonly setor: string;
  readonly dia: string;
  readonly bloco: BlocoDaOperacao;
  readonly duracaoMin: number | null;
  readonly pausadoMin: number;
  readonly ativoMin: number | null;
  readonly emPausa: boolean;
  readonly produtos: readonly ProdutoDoBloco[];
  readonly eventos: readonly {
    readonly em: string;
    readonly tipo: string;
    readonly quem: string | null;
    readonly dados: Record<string, unknown>;
  }[];
}
/** Quais produtos entram no Giro do setor (dono, 26/09). */
export interface CapturaDoSetor {
  /** 100 = só vendidos; 0 = só não vendidos; entre = mistura. */
  readonly pctVendidos: number;
  /** Janela de vendas: de N até M dias atrás (1..1 = ontem). */
  readonly diasInicio: number;
  readonly diasFim: number;
  /** Tamanho do Giro do setor no dia; null = quantos venderam. */
  readonly total: number | null;
}
export interface SetorDoGiro {
  readonly id: string;
  readonly nome: string;
  readonly descricao: string | null;
  readonly grupos: readonly string[];
  readonly classificacoes: readonly string[];
  readonly ordem: number;
  readonly qtdBlocos: number;
  readonly blocosPorPessoas: boolean;
  readonly divisao: 'LETRA' | 'LABORATORIO';
  readonly captura: CapturaDoSetor;
  readonly permitirQuebraLetra: boolean;
  readonly alvo: number | null;
  readonly exigeEan: boolean;
  readonly fotoTaxa: number;
  readonly gera: boolean;
  readonly ativo: boolean;
  readonly versao: number;
  readonly vinculados: number;
  readonly ultimaOperacao: string | null;
  readonly blocos: readonly {
    readonly numero: number;
    readonly ativo: boolean;
    readonly ultimoUso: string | null;
  }[];
}
export interface ItemDoPlano {
  readonly productId: string;
  readonly codigo: string | null;
  readonly nome: string;
  readonly qtdVendida: number;
  /** Média de venda diária nos 30 dias antes do Giro. */
  readonly mediaDiaria: number;
  readonly origem: 'VENDIDO' | 'NAO_VENDIDO';
  readonly alvo: number;
  readonly estoque: number | null;
  readonly estoqueConfiavel: boolean;
  readonly fotoExigida: boolean;
}
export interface BlocoDoPlano {
  readonly numero: number;
  readonly faixaInicial: string | null;
  readonly faixaFinal: string | null;
  readonly rotulo: string;
  readonly primeiro: string | null;
  readonly ultimo: string | null;
  readonly total: number;
  readonly letraQuebrada: boolean;
  readonly responsavel: { readonly accountId: string; readonly nome: string } | null;
  readonly segundoBloco: boolean;
  readonly porque: string | null;
  readonly itens: readonly ItemDoPlano[];
}
export interface Exclusao {
  readonly productId: string | null;
  readonly codigo: string | null;
  readonly nome: string | null;
  readonly motivo: MotivoForaDaMissao;
  readonly vendido: number;
  readonly estoque: number | null;
  readonly detalhe: string | null;
}
export interface PlanoDoDia {
  readonly dia: string;
  readonly vendasDe: string;
  readonly setor: { readonly id: string; readonly nome: string; readonly versao: number };
  readonly config: {
    readonly versaoGiro: number;
    readonly qtdBlocos: number;
    readonly permitirQuebraLetra: boolean;
    readonly alvoSetor: number | null;
    readonly alvoGlobal: number;
    readonly exigeEan: boolean;
  };
  readonly vendidos: number;
  readonly elegiveis: number;
  readonly cargaAlvo: number;
  readonly blocos: readonly BlocoDoPlano[];
  readonly exclusoes: readonly Exclusao[];
  readonly pessoas: readonly {
    readonly accountId: string;
    readonly nome: string;
    readonly estado: string;
    readonly peso: number;
    readonly ciclo: number;
    readonly concluidos: readonly number[];
    readonly reservados: readonly number[];
  }[];
  readonly semBloco: readonly { readonly accountId: string; readonly nome: string }[];
  readonly avisos: readonly string[];
  readonly vendasLidas: LeituraDasVendas;
  readonly fonteEstoque: { readonly capturadoAte: string | null; readonly indisponiveis: number };
}
export interface PessoaDaEquipe {
  readonly accountId: string;
  readonly nome: string;
  readonly perfil: string;
  readonly setores: readonly { readonly id: string; readonly nome: string }[];
  readonly presenca: 'PRESENTE' | 'AUSENTE' | 'PARCIAL';
  readonly presencaRegistrada: boolean;
  /** Vínculo com o setor pedido já encerrado: vale até este dia (null = sem fim). */
  readonly vinculoAte: string | null;
  readonly peso: number;
  readonly blocosHoje: readonly {
    readonly numero: number;
    readonly setor: string;
    readonly recebido: boolean;
    readonly etapa: string;
  }[];
  readonly carga: number;
  readonly pendentesRecebidos: number;
  readonly ciclo: {
    readonly feitos: number;
    readonly total: number;
    readonly numero: number;
  } | null;
  readonly semBlocoHoje: boolean;
}
export interface EquipeDoGiro {
  readonly dia: string;
  readonly pessoas: readonly PessoaDaEquipe[];
  readonly vinculados: number;
  readonly presentes: number;
  readonly ausentes: number;
  readonly parciais: number;
  readonly semBloco: number;
  readonly blocosComCarga: number;
}
export interface MatrizDoRodizio {
  readonly setorId: string;
  readonly blocos: number;
  readonly cicloDesde: string | null;
  readonly linhas: readonly {
    readonly accountId: string;
    readonly nome: string;
    readonly ausente: boolean;
    readonly celulas: readonly ('concluido' | 'hoje' | 'parcial' | null)[];
    readonly feitos: number;
    readonly total: number;
    readonly ciclo: number;
    readonly proximos: readonly number[];
  }[];
}
export interface ExcecoesDoGiro {
  readonly dia: string;
  readonly vendasDe: string;
  readonly itens: readonly (Exclusao & { readonly setor: string | null })[];
  readonly eansDesconhecidos: readonly {
    readonly ean: string;
    readonly em: string;
    readonly quem: string | null;
    readonly produtoEsperado: string | null;
  }[];
  readonly contagem: Readonly<Record<string, number>>;
}
export interface HistoricoDoGiro {
  readonly dias: readonly {
    readonly dia: string;
    readonly setores: number;
    readonly blocos: number;
    readonly skus: number;
    readonly equipe: number;
    readonly conclusao: number | null;
    readonly estado: 'em_andamento' | 'concluida' | 'cancelada' | 'rascunho';
  }[];
}
export interface ConfigDoGiro {
  readonly alvo_global: number;
  readonly estoque_minimo: number;
  readonly estoque_idade_max_min: number;
  readonly parcial_fator: number;
  readonly fora_recente_dias: number;
  readonly divergencia_sla_horas: number;
  readonly periodo_d1_dias: number;
  readonly bloqueia_inovapay: boolean;
  readonly foto_aleatoria: boolean;
  readonly fotos_fixa_por_missao: number;
  readonly conflito_multissetor: string;
  readonly geracao_automatica: boolean;
  readonly geracao_horario: string;
  readonly geracao_dias: readonly number[];
  /** Meta de tempo da missão em minutos; 0 = sem meta. */
  readonly tempo_meta_min: number;
  /** Controlados entram no Giro? */
  readonly incluir_controlados: boolean;
  /** Alvo de exposição automático: o maior dia de venda do mês anterior (o número do Giro MAX). */
  readonly alvo_automatico: boolean;
  /** Horário do Giro por balconista (accountId → horário); dono, 28/09. */
  readonly horarios: Readonly<Record<string, HorarioDoGiro>>;
  /** Quem não tem horário: 'dia_todo' (vale desde cedo) ou 'livre' (não trava). */
  readonly sem_horario: 'dia_todo' | 'livre';
  /** Exceção de captura por dia: { '2026-09-29': '2026-09-26' } (dono, 28/09). */
  readonly excecoes_captura?: Readonly<Record<string, string>>;
  /** Palavras que tiram o produto do Giro pelo nome, além da lista fixa (dono, 01/10). */
  readonly fora_do_giro_nomes?: readonly string[];
  readonly versao: number;
}
export const salvarExcecaoDeCaptura = (
  s: Sessao,
  dia: string,
  desde: string | null,
  motivo: string,
) => escrever<{ versao: number }>(s, 'PUT', '/api/giro/excecao-captura', { dia, desde, motivo });
export interface HorarioDoGiro {
  readonly padrao: string | null;
  /** Troca o horário num dia da semana ('0' = domingo … '6' = sábado). */
  readonly dias?: Readonly<Partial<Record<string, string>>>;
}
export const salvarHorariosDoGiro = (
  s: Sessao,
  horarios: Record<string, HorarioDoGiro>,
  semHorario: 'dia_todo' | 'livre',
  motivo: string,
) =>
  escrever<{ versao: number }>(s, 'PUT', '/api/giro/horarios', {
    horarios,
    sem_horario: semHorario,
    motivo,
  });
export type ConfigEditavelDoGiro = Pick<
  ConfigDoGiro,
  | 'alvo_global'
  | 'estoque_minimo'
  | 'bloqueia_inovapay'
  | 'foto_aleatoria'
  | 'fotos_fixa_por_missao'
  | 'divergencia_sla_horas'
  | 'geracao_automatica'
  | 'geracao_horario'
  | 'geracao_dias'
  | 'tempo_meta_min'
  | 'incluir_controlados'
  | 'alvo_automatico'
> & { readonly fora_do_giro_nomes: readonly string[] };
export const salvarConfigDoGiro = (s: Sessao, valor: ConfigEditavelDoGiro, motivo: string) =>
  escrever<{ versao: number }>(s, 'PUT', '/api/giro/config', { valor, motivo });

/* --- balconista --- */

export type FaseDoBloco =
  'nao_iniciado' | 'contagem' | 'reposicao' | 'concluido' | 'aguardando_sistema' | 'cancelado';
export interface MeuBloco {
  readonly missaoId: string;
  readonly setor: string;
  readonly setorId: string;
  readonly numero: number;
  readonly faixa: string;
  readonly total: number;
  readonly contados: number;
  readonly aRepor: number;
  readonly repostos: number;
  readonly aguardandoEstoque: number;
  readonly unidadesARepor: number;
  readonly divergencias: number;
  readonly fotosPendentes: number;
  readonly estado: string;
  readonly fase: FaseDoBloco;
  readonly iniciadaEm: string | null;
  readonly contagemConcluidaEm: string | null;
  readonly concluidaEm: string | null;
  /** Só "Bloco recebido" — sem nome de quem passou nem quanto o colega contou (dono, 27/09). */
  readonly recebido: { readonly em: string } | null;
  readonly cancelamento: { readonly em: string; readonly motivo: string } | null;
}
export interface MeuGiro {
  readonly dia: string;
  readonly nome: string;
  readonly situacao:
    'sem_missao' | 'aguardando_publicacao' | 'em_andamento' | 'liberado' | 'cancelado';
  readonly bloqueiaInovapay: boolean;
  readonly liberado: boolean;
  /** Horário do Giro desta pessoa hoje ('HH:MM'); null = sem horário (dono, 28/09). */
  readonly horario: string | null;
  /** Ainda não é a hora: vende normal e o Giro não começa. */
  readonly antesDoHorario: boolean;
  readonly blocos: readonly MeuBloco[];
  readonly ciclos: readonly {
    readonly setor: string;
    readonly feitos: number;
    readonly total: number;
  }[];
  readonly setorSemMissao: {
    readonly setor: string;
    readonly blocos: number;
    readonly presentes: number;
  } | null;
  readonly transferidosDeMim: readonly {
    readonly numero: number;
    readonly setor: string;
    readonly em: string;
    readonly contados: number;
    readonly pendentes: number;
  }[];
}
export type SituacaoDoItem =
  | 'pendente'
  | 'contado'
  | 'aguardando_estoque'
  | 'nao_localizado'
  | 'foto_pendente'
  | 'reposto'
  | 'diferenca'
  | 'transferido';
export interface ItemDaLista {
  readonly id: string;
  readonly ordem: number;
  readonly nome: string;
  readonly codigo: string | null;
  readonly situacao: SituacaoDoItem;
  readonly contadoEm: string | null;
  readonly fotoEnviada: boolean;
  /** Produto completo para o balconista achar a caixa (dono, 28/09). */
  readonly ean: string | null;
  readonly fabricante: string | null;
  /** Foto do InovaPED por EAN exato; null = sem foto. */
  readonly fotoProduto: string | null;
}
export interface MissaoDoBalconista {
  readonly missaoId: string;
  readonly setor: string;
  readonly numero: number;
  readonly faixa: string;
  readonly estado: string;
  readonly exigeEan: boolean;
  readonly pausa: PausaAberta | null;
  /** Meta de tempo do gestor (minutos); null = sem meta. */
  readonly metaMin: number | null;
  /** Tempo ativo até agora (sem pausas); null = ainda não começou. */
  readonly ativoMin: number | null;
  /** Foto da caixa do que foi separado para repor (dono, 29/09): exigida para finalizar. */
  readonly fotoDaCaixa: 'nao_precisa' | 'pendente' | 'enviada';
  readonly itens: readonly ItemDaLista[];
}
export type ResultadoDaLeitura =
  | { readonly resultado: 'confirmado'; readonly entrada: 'SCAN' | 'MANUAL' }
  | {
      readonly resultado: 'produto_diferente';
      readonly lido: {
        readonly nome: string;
        readonly codigo: string | null;
        readonly ean: string;
      };
      readonly esperado: { readonly nome: string; readonly ean: string | null };
    }
  | { readonly resultado: 'nao_reconhecido'; readonly codigo: string };
export interface ResultadoDaContagem {
  readonly itemId: string;
  readonly prateleira: number;
  readonly naoLocalizado: boolean;
  readonly alvo: number;
  readonly estoque: number | null;
  readonly estoqueTexto: string;
  readonly repor: number;
  readonly limitadoPor: string | null;
  readonly aguardandoEstoque: boolean;
  readonly fotoExigida: boolean;
  readonly inconsistencia: string | null;
  readonly contadoEm: string;
  readonly porque: string;
}
export interface ResumoDaContagem {
  readonly conferidos: number;
  readonly total: number;
  readonly precisamRepor: number;
  readonly unidades: number;
  readonly aguardandoEstoque: number;
  readonly fotos: number;
  readonly naoLocalizados: number;
  readonly iniciadaEm: string | null;
  readonly concluidaEm: string | null;
}
export interface ItemDaReposicao {
  readonly id: string;
  readonly nome: string;
  readonly codigo: string | null;
  readonly prateleira: number;
  readonly alvo: number;
  readonly estoque: number | null;
  readonly repor: number;
  readonly encontrado: number | null;
  readonly reposto: number | null;
  readonly feito: boolean;
  /** Produto completo para achar a caixa no estoque (dono, 29/09). */
  readonly ean: string | null;
  readonly fabricante: string | null;
  readonly fotoProduto: string | null;
}
export interface ResultadoDaReposicao {
  readonly pedido: number;
  readonly encontrado: number;
  readonly reposto: number;
  readonly prateleiraAgora: number;
  readonly alvo: number;
  readonly divergencia: boolean;
  readonly porque: string;
}

/* ------------------------------------------------------------------ *
 * Chamadas
 * ------------------------------------------------------------------ */

const q = (o: Record<string, string | null | undefined>) =>
  new URLSearchParams(Object.entries(o).filter((e): e is [string, string] => !!e[1])).toString();

export const lerOperacao = (dia?: string) => ler<OperacaoDoDia>(`/api/giro/operacao?${q({ dia })}`);
export const lerBloco = (geracaoId: string, numero: number) =>
  ler<DetalheDoBloco>(`/api/giro/blocos/${geracaoId}/${String(numero)}`);
export const lerSetores = () =>
  ler<{ setores: SetorDoGiro[]; alvoGlobal: number; gruposSemSetor: string[] }>(
    '/api/giro/setores',
  );
export const lerEquipe = (dia?: string, setor?: string | null) =>
  ler<EquipeDoGiro>(`/api/giro/equipe?${q({ dia, setor })}`);
export const lerRodizio = (setor: string, dia?: string) =>
  ler<MatrizDoRodizio>(`/api/giro/rodizio?${q({ setor, dia })}`);
export const lerExcecoesDoGiro = (dia?: string) =>
  ler<ExcecoesDoGiro>(`/api/giro/excecoes?${q({ dia })}`);
export const lerHistoricoDoGiro = () => ler<HistoricoDoGiro>('/api/giro/historico');
export const lerConfigDoGiro = () => ler<ConfigDoGiro>('/api/giro/config');

export const simular = (s: Sessao, setorId: string, dia: string) =>
  escrever<PlanoDoDia>(s, 'POST', '/api/giro/simular', { setorId, dia });
export const gerar = (s: Sessao, setorId: string, dia: string) =>
  escrever<{ geracaoId: string; plano: PlanoDoDia }>(s, 'POST', '/api/giro/geracoes', {
    setorId,
    dia,
  });
export const publicar = (s: Sessao, geracaoId: string, confirmarVendasIncompletas = false) =>
  escrever<null>(
    s,
    'POST',
    `/api/giro/geracoes/${geracaoId}/publicar`,
    confirmarVendasIncompletas ? { confirmarVendasIncompletas: true } : {},
  );
export const cancelar = (s: Sessao, geracaoId: string, motivo: string) =>
  escrever<null>(s, 'POST', `/api/giro/geracoes/${geracaoId}/cancelar`, { motivo });
export const transferir = (s: Sessao, missaoId: string, paraAccountId: string, motivo: string) =>
  escrever<{
    pendentes: number;
    contados: number;
    troca: { readonly missaoId: string; readonly numero: number } | null;
  }>(s, 'POST', `/api/giro/missoes/${missaoId}/transferir`, { paraAccountId, motivo });
export const salvarSetor = (
  s: Sessao,
  dados: Omit<SetorDoGiro, 'id' | 'versao' | 'vinculados' | 'ultimaOperacao' | 'blocos'> & {
    id?: string | null;
    versaoEsperada?: number | null;
  },
) => escrever<{ id: string; versao: number }>(s, 'POST', '/api/giro/setores', dados);
/** Meu Giro (balconista): cada dia com a nota e a mensagem. */
export interface DiaDoMeuGiro {
  readonly dia: string;
  readonly blocos: number;
  readonly concluidos: number;
  /** Blocos que o gestor passou para outra pessoa: ficam no dia, não contam como "não terminado". */
  readonly transferidos?: number;
  readonly conferidos: number;
  readonly naoLocalizados: number;
  readonly repostosUnidades: number;
  readonly paraRepor: number;
  readonly deixouDeRepor: number;
  readonly divergencias: number;
  readonly ativoMin: number | null;
  readonly metaMin: number | null;
  readonly nota: number;
  readonly mensagem: string;
  /** Hoje, bloco por terminar: sem nota ainda. */
  readonly emAndamento?: boolean;
}
export const lerMeuPainelDoGiro = (dias = 7) =>
  ler<{ dias: readonly DiaDoMeuGiro[]; media: number | null }>(
    `/api/giro/meu-painel?dias=${String(dias)}`,
  );

/** Painel do Giro (gestor). */
export interface PainelDoGiro {
  readonly periodo: { readonly de: string; readonly ate: string };
  readonly setores: readonly {
    readonly id: string;
    readonly nome: string;
    readonly cadastrados: number;
    readonly comEstoque: number;
    readonly jaPassaram: number;
    readonly noPeriodo: number;
  }[];
  readonly balconistas: readonly {
    readonly accountId: string;
    readonly nome: string;
    readonly missoes: number;
    readonly concluidas: number;
    readonly produtos: number;
    readonly ativoMin: number;
    readonly minPorProduto: number | null;
    readonly divergencias: number;
  }[];
  readonly maisGiram: readonly {
    readonly productId: string;
    readonly nome: string;
    readonly setor: string | null;
    readonly mediaDiaria: number;
    readonly vezesNoGiro: number;
  }[];
}
export const lerPainelDoGiro = (dias = 30) =>
  ler<PainelDoGiro>(`/api/giro/painel?dias=${String(dias)}`);

/** "Não cabe 6": o que a equipe declarou, por produto (o gestor confirma ou recusa). */
export interface ProdutoComCapacidadeDeclarada {
  readonly productId: string;
  readonly nome: string;
  readonly codigo: string | null;
  readonly alvo: number;
  readonly menorCapacidade: number;
  readonly declaracoes: readonly {
    readonly id: string;
    readonly nome: string;
    readonly capacidade: number;
    readonly prateleira: number;
    readonly em: string;
  }[];
}
export interface BloqueioDeVenda {
  readonly id: string;
  readonly accountId: string;
  readonly nome: string;
  readonly dia: string;
  readonly porque: string;
  readonly criadoEm: string;
  readonly liberado: { readonly em: string; readonly por: string; readonly motivo: string } | null;
}
export const lerCapacidades = () =>
  ler<readonly ProdutoComCapacidadeDeclarada[]>('/api/giro/capacidades');
export const decidirCapacidade = (
  s: Sessao,
  productId: string,
  d: { decisao: 'CONFIRMAR' | 'RECUSAR'; capacidade: number | null; motivo: string },
) => escrever<{ decididas: number }>(s, 'POST', `/api/giro/capacidades/${productId}`, d);
export const lerBloqueiosDeVenda = () =>
  ler<readonly BloqueioDeVenda[]>('/api/giro/bloqueios-venda');
export const liberarBloqueioDeVenda = (s: Sessao, id: string, motivo: string) =>
  escrever<null>(s, 'POST', `/api/giro/bloqueios-venda/${id}/liberar`, { motivo });
/* ------------------------------ Bloqueados (Carlos + dono, 08/10) ------------------------------ */

export type OrigemDoBloqueio = 'POR_ERRO' | 'PERCENTUAL' | 'MANUAL';
export type SituacaoDoBloqueio = 'AGENDADO' | 'BLOQUEADO' | 'LIBERADO_PELO_GESTOR' | 'TERMINOU';
export type NivelDoBloqueio = 'SEM_REPOSICAO' | 'OK' | 'PERTO' | 'POUCOS_ITENS' | 'BLOQUEIA';
export interface RegraDoBloqueio {
  readonly ativo: boolean;
  readonly limitePct: number;
  readonly minimoMandados: number;
  readonly desde: string;
  readonly versao: number;
  readonly motivo: string | null;
  readonly vigenteDesde: string | null;
}
export interface BloqueioDaLista {
  readonly id: string;
  readonly accountId: string;
  readonly nome: string;
  readonly origem: OrigemDoBloqueio;
  readonly situacao: SituacaoDoBloqueio;
  readonly dia: string;
  readonly ate: string | null;
  readonly diaGiro: string | null;
  readonly erros: number | null;
  readonly mandados: number | null;
  readonly taxa: number | null;
  readonly limite: number | null;
  readonly mensagem: string;
  readonly porque: string;
  readonly motivo: string | null;
  readonly criadoEm: string;
  readonly criadoPor: string | null;
  readonly liberado: { readonly em: string; readonly por: string; readonly motivo: string } | null;
  readonly terminaEm: string | null;
}
export interface LinhaDoDiaDeBloqueio {
  readonly accountId: string;
  readonly nome: string;
  readonly setores: readonly string[];
  readonly itens: number;
  readonly contados: number;
  readonly mandados: number;
  readonly repostos: number;
  readonly erros: number;
  readonly corretos: number;
  readonly esperando: number;
  readonly outros: number;
  readonly taxa: number;
  readonly taxaMaxima: number;
  readonly nivel: NivelDoBloqueio;
  readonly bloqueio: BloqueioDaLista | null;
}
export interface PainelDeBloqueiosDoDia {
  readonly dia: string;
  readonly regra: RegraDoBloqueio;
  readonly foraDaRegra: boolean;
  readonly linhas: readonly LinhaDoDiaDeBloqueio[];
}
export interface CasoDaReposicao {
  readonly divergenciaId: string;
  readonly numero: number | null;
  readonly produto: string;
  readonly codigo: string | null;
  readonly pedido: number;
  readonly encontrado: number;
  readonly recontagem: number | null;
  readonly estado: string;
  readonly resultado: string | null;
  readonly auditor: string | null;
  readonly auditadoEm: string | null;
}
export const lerBloqueadosDoDia = (dia: string) =>
  ler<PainelDeBloqueiosDoDia>(`/api/giro/bloqueados/dia?${q({ dia })}`);
export const lerCasosDaReposicao = (conta: string, dia: string) =>
  ler<readonly CasoDaReposicao[]>(`/api/giro/bloqueados/casos?${q({ conta, dia })}`);
export const lerListaDeBloqueados = (f: { de?: string; ate?: string } = {}) =>
  ler<readonly BloqueioDaLista[]>(`/api/giro/bloqueados?${q({ de: f.de, ate: f.ate })}`);
export const bloquearVenda = (s: Sessao, d: { conta: string; dia: string; motivo: string }) =>
  escrever<{ id: string }>(s, 'POST', '/api/giro/bloqueados', d);
export const lerRegraDoBloqueio = () => ler<RegraDoBloqueio>('/api/giro/bloqueados/regra');
export const salvarRegraDoBloqueio = (
  s: Sessao,
  d: { ativo: boolean; limitePct: number; minimoMandados: number; motivo: string },
) => escrever<{ versao: number }>(s, 'PUT', '/api/giro/bloqueados/regra', d);

/* ------------------------------ Não cabe 3.0 (Carlos, 08/10) ------------------------------ */

export type CorDaConferencia = 'VERDE' | 'AMARELO' | 'VERMELHO' | 'CINZA';
export interface GiroMaximo {
  readonly qtd: number;
  readonly dia: string;
  readonly noPeriodo: number;
  readonly diasComVenda: number;
}
export interface PeriodoDoGiroMaximo {
  readonly de: string;
  readonly ate: string;
  readonly dias: number;
}
export interface ContagemDoItem {
  readonly itemId: string;
  readonly diaGiro: string;
  readonly setor: string;
  readonly pessoa: string;
  readonly contadoEm: string | null;
  readonly prateleira: number | null;
  readonly achouNoEstoque: number | null;
  readonly repor: number | null;
  readonly reposto: number | null;
  readonly alvo: number;
  readonly estoqueHos: number | null;
  readonly estoqueNaGeracao: number | null;
  readonly limitadoPor: string | null;
  readonly capacidadeDeclarada: number | null;
  readonly divergencia: {
    readonly id: string;
    readonly numero: number | null;
    readonly tipo: 'FALTOU_NA_REPOSICAO' | 'ESTOQUE_INCONSISTENTE';
    readonly estado: string;
    readonly resultado: string | null;
    readonly auditor: string | null;
    readonly auditadoEm: string | null;
  } | null;
  readonly conferencia: {
    readonly necessidade: 'SIM' | 'NAO' | 'SEM_DADOS';
    readonly sugerido: number;
    readonly visto: number | null;
    readonly diferenca: number | null;
    readonly estoque:
      'COMPATIVEL' | 'PRATELEIRA_MAIOR_QUE_HOS' | 'NAO_ACHOU_O_QUE_HOS_TEM' | 'SEM_REGISTRO';
    readonly cor: CorDaConferencia;
    readonly situacao: string;
  };
}
export type SituacaoDaDeclaracao = 'PENDENTE' | 'CONFIRMADA' | 'RECUSADA' | 'TODAS';
export interface DeclaracaoDoNaoCabe {
  readonly id: string;
  readonly nome: string;
  readonly capacidade: number;
  readonly prateleira: number;
  readonly reposto: number;
  readonly alvo: number;
  readonly em: string;
  readonly estado: Exclude<SituacaoDaDeclaracao, 'TODAS'>;
  readonly decisao: { readonly por: string; readonly em: string; readonly motivo: string } | null;
}
export interface ProdutoDoNaoCabe {
  readonly productId: string;
  readonly nome: string;
  readonly codigo: string | null;
  readonly ean: string | null;
  readonly fabricante: string | null;
  readonly alvo: number;
  readonly menorCapacidade: number;
  readonly pendentes: number;
  readonly declaracoesNoPeriodo: number;
  readonly divergenciasNoPeriodo: number;
  readonly ultimaDeclaracao: string;
  readonly declaracoes: readonly DeclaracaoDoNaoCabe[];
  readonly giroMaximo: GiroMaximo | null;
  readonly ultimaContagem: ContagemDoItem | null;
}
export interface PainelDoNaoCabe {
  readonly de: string;
  readonly ate: string;
  readonly giro: PeriodoDoGiroMaximo;
  readonly indicadores: {
    readonly bloqueadosAgora: number;
    readonly liberadosNoPeriodo: number;
    readonly errosDeReposicao: number;
    readonly divergenciasAbertas: number;
    readonly produtosComCapacidade: number;
    readonly produtosPrecisandoRepor: number;
  };
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
  readonly produtos: readonly ProdutoDoNaoCabe[];
}
export interface HistoricoDoProdutoNoGiro {
  readonly produto: {
    readonly productId: string;
    readonly nome: string;
    readonly codigo: string | null;
    readonly ean: string | null;
    readonly fabricante: string | null;
    readonly grupo: string | null;
    readonly categoria: string | null;
    readonly classificacao: string | null;
    readonly estoqueAtual: number | null;
  };
  readonly giro: PeriodoDoGiroMaximo;
  readonly giroMaximo: GiroMaximo | null;
  readonly contagens: readonly ContagemDoItem[];
  readonly declaracoes: readonly (DeclaracaoDoNaoCabe & { readonly diaGiro: string | null })[];
}
export interface FiltrosDoNaoCabe {
  readonly de?: string | undefined;
  readonly ate?: string | undefined;
  readonly busca?: string | undefined;
  readonly conta?: string | undefined;
  readonly situacao?: SituacaoDaDeclaracao | undefined;
  readonly divergencia?:
    'TODAS' | 'COM' | 'SEM' | 'FALTOU_NA_REPOSICAO' | 'ESTOQUE_INCONSISTENTE' | undefined;
  readonly ordem?: 'DECLARACOES' | 'DIVERGENCIAS' | 'PRODUTO' | 'DATA' | undefined;
  readonly pagina?: number | undefined;
  readonly giroDias?: number | undefined;
}
export const lerNaoCabe = (f: FiltrosDoNaoCabe) =>
  ler<PainelDoNaoCabe>(
    `/api/giro/nao-cabe?${q({
      de: f.de,
      ate: f.ate,
      busca: f.busca,
      conta: f.conta,
      situacao: f.situacao,
      divergencia: f.divergencia,
      ordem: f.ordem,
      pagina: f.pagina ? String(f.pagina) : undefined,
      giroDias: f.giroDias ? String(f.giroDias) : undefined,
    })}`,
  );
export const lerHistoricoDoProdutoNoGiro = (productId: string, giroDias: number) =>
  ler<HistoricoDoProdutoNoGiro>(
    `/api/giro/nao-cabe/produto/${productId}?${q({ giroDias: String(giroDias) })}`,
  );

export const criarSetoresPadrao = (s: Sessao) =>
  escrever<{ criados: string[] }>(s, 'POST', '/api/giro/setores/padrao', {});
export const vincular = (s: Sessao, setorId: string, accountId: string) =>
  escrever<null>(s, 'POST', `/api/giro/setores/${setorId}/membros`, { accountId });
export const encerrarVinculo = (s: Sessao, setorId: string, accountId: string) =>
  escrever<null>(s, 'POST', `/api/giro/setores/${setorId}/membros/${accountId}/encerrar`);
export const salvarPresencas = (
  s: Sessao,
  dia: string,
  itens: { accountId: string; estado: 'PRESENTE' | 'AUSENTE' | 'PARCIAL'; fator?: number | null }[],
) => escrever<null>(s, 'PUT', '/api/giro/presencas', { dia, itens });

/** Folgas (presença AUSENTE marcada no calendário) entre `de` e `ate`. */
export const lerFolgas = (de: string, ate: string) =>
  ler<{ folgas: readonly { accountId: string; nome: string; dia: string }[] }>(
    `/api/giro/folgas?${q({ de, ate })}`,
  );

export const lerMeuGiro = () => ler<MeuGiro>('/api/giro/meu');
export const lerMissao = (id: string) => ler<MissaoDoBalconista>(`/api/giro/missoes/${id}`);
export const lerResumo = (id: string) => ler<ResumoDaContagem>(`/api/giro/missoes/${id}/resumo`);
export const lerReposicao = (id: string) =>
  ler<{ itens: ItemDaReposicao[]; aguardando: string[]; unidades: number }>(
    `/api/giro/missoes/${id}/reposicao`,
  );
export const lerCodigo = (s: Sessao, itemId: string, codigo: string, entrada: 'SCAN' | 'MANUAL') =>
  escrever<ResultadoDaLeitura>(s, 'POST', `/api/giro/itens/${itemId}/leitura`, { codigo, entrada });
export const contar = (s: Sessao, itemId: string, quantidade: number, naoLocalizado = false) =>
  escrever<ResultadoDaContagem>(s, 'POST', `/api/giro/itens/${itemId}/contagem`, {
    quantidade,
    naoLocalizado,
  });
export const fotoDoItem = (s: Sessao, itemId: string, evidenciaId: string) =>
  escrever<null>(s, 'POST', `/api/giro/itens/${itemId}/foto`, { evidenciaId });
export interface ResultadoDoPulo {
  readonly itemId: string;
  readonly ordem: number;
  readonly pendentes: number;
}
/** Pular produto (dono, 01/10): vai para o fim da fila do bloco e continua pendente. */
export const pularItem = (s: Sessao, itemId: string) =>
  escrever<ResultadoDoPulo>(s, 'POST', `/api/giro/itens/${itemId}/pular`);
/** Pular no estoque (dono, 13/10): o produto da reposição vai para o fim da fila. */
export const pularNaReposicao = (s: Sessao, itemId: string) =>
  escrever<ResultadoDoPulo>(s, 'POST', `/api/giro/itens/${itemId}/pular-reposicao`);
/** Editar o Giro de hoje (dono, 13/10): só prateleira e achado no estoque, antes de finalizar. */
export interface ItemParaEditar {
  readonly id: string;
  readonly nome: string;
  readonly codigo: string | null;
  readonly ordem: number;
  readonly prateleira: number;
  readonly naoLocalizado: boolean;
  readonly pedido: number | null;
  readonly encontrado: number | null;
  readonly hosAgora: number | null;
  readonly hosLidoEm: string | null;
  readonly podeEditarPrateleira: boolean;
  readonly podeEditarEncontrado: boolean;
  readonly travado: string | null;
  readonly editado: boolean;
}
export interface ResultadoDaEdicao {
  readonly itemId: string;
  readonly prateleira: number;
  readonly pedido: number | null;
  readonly encontrado: number | null;
  readonly falta: number;
  readonly casosCorrigidos: number;
  readonly mudou: boolean;
}
export const lerParaEditar = (id: string) =>
  ler<{ editavel: boolean; itens: ItemParaEditar[] }>(`/api/giro/missoes/${id}/editar`);
export const editarItem = (
  s: Sessao,
  itemId: string,
  d: { prateleira?: number; encontrado?: number },
) => escrever<ResultadoDaEdicao>(s, 'POST', `/api/giro/itens/${itemId}/editar`, d);
export const fotoDaReposicao = (s: Sessao, missaoId: string, evidenciaId: string) =>
  escrever<null>(s, 'POST', `/api/giro/missoes/${missaoId}/foto-reposicao`, { evidenciaId });
export const concluirContagem = (s: Sessao, missaoId: string) =>
  escrever<ResumoDaContagem>(s, 'POST', `/api/giro/missoes/${missaoId}/concluir-contagem`);
export const iniciarReposicao = (s: Sessao, missaoId: string) =>
  escrever<null>(s, 'POST', `/api/giro/missoes/${missaoId}/iniciar-reposicao`);
export const repor = (
  s: Sessao,
  itemId: string,
  encontrado: number,
  reposto: number,
  /** "Coloquei só o que cabe": quantos cabem na prateleira. */
  capacidade: number | null = null,
) =>
  escrever<ResultadoDaReposicao>(s, 'POST', `/api/giro/itens/${itemId}/reposicao`, {
    encontrado,
    reposto,
    ...(capacidade !== null ? { capacidade } : {}),
  });
export const finalizar = (s: Sessao, missaoId: string) =>
  escrever<{ estado: string; liberado: boolean }>(
    s,
    'POST',
    `/api/giro/missoes/${missaoId}/finalizar`,
  );

/* ------------------------------------------------------------------ *
 * Formatos
 * ------------------------------------------------------------------ */

export function hojeBrasilia(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
}
export const diaCurto = (d: string) => d.split('-').reverse().slice(0, 2).join('/');
/** "26/09 a 27/09" quando a operação usa mais de um dia de vendas (segunda com domingo sem Giro). */
export const vendasUsadas = (o: { readonly vendasDe: string; readonly vendasDesde: string }) =>
  o.vendasDesde < o.vendasDe
    ? `${diaCurto(o.vendasDesde)} a ${diaCurto(o.vendasDe)}`
    : diaCurto(o.vendasDe);
export const diaLongo = (d: string) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
export const diaDaSemana = (d: string) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString('pt-BR', { weekday: 'long', timeZone: 'UTC' });
export const bN = (n: number) => `B${String(n)}`;
export const bloco2 = (n: number) => `Bloco ${String(n).padStart(2, '0')}`;
export const iniciais = (nome: string) =>
  nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
export const primeiroNome = (nome: string | null | undefined) => (nome ?? '').split(' ')[0] ?? '';
export const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) : 0);

export const ROTULO_DA_ETAPA: Record<EtapaDoBloco, string> = {
  SEM_CARGA: 'Sem carga',
  AGUARDANDO_RESPONSAVEL: 'Sem responsável',
  RASCUNHO: 'Rascunho',
  NAO_INICIADO: 'Não iniciado',
  CONTAGEM: 'Em contagem',
  REPOSICAO: 'Reposição',
  AUDITORIA_PENDENTE: 'Aguarda auditoria',
  CONCLUIDO: 'Concluído',
  PARCIAL: 'Parcial',
  CANCELADO: 'Cancelado',
};
export const ROTULO_DO_MOTIVO: Record<MotivoForaDaMissao, string> = {
  ESTOQUE_ZERO: 'Estoque zero',
  ESTOQUE_INDISPONIVEL: 'Estoque indisponível',
  ESTOQUE_STALE: 'Estoque desatualizado',
  SEM_SETOR: 'Sem setor',
  SEM_CADASTRO: 'Sem cadastro',
  SETOR_SEM_EQUIPE: 'Setor sem equipe',
};

/* ------------------------------------------------------------------ *
 * Ocorrências do corredor (§37 do 1.0)
 * ------------------------------------------------------------------ */

export type TipoDeOcorrencia =
  | 'PRODUTO_LUGAR_ERRADO'
  | 'ETIQUETA_AUSENTE'
  | 'PRECO_DIVERGENTE'
  | 'EMBALAGEM_AVARIADA'
  | 'VALIDADE'
  | 'CAPACIDADE_DIFERENTE'
  | 'EAN_NAO_CADASTRADO'
  | 'OUTRO';

/** Rótulo e o que o campo "valor" pede em cada tipo (vazio = o tipo não tem valor). */
export const TIPOS_DE_OCORRENCIA: readonly {
  readonly tipo: TipoDeOcorrencia;
  readonly rotulo: string;
  readonly valor: string;
}[] = [
  { tipo: 'PRODUTO_LUGAR_ERRADO', rotulo: 'Produto no lugar errado', valor: '' },
  { tipo: 'ETIQUETA_AUSENTE', rotulo: 'Etiqueta ausente', valor: '' },
  { tipo: 'PRECO_DIVERGENTE', rotulo: 'Preço divergente', valor: 'Preço na etiqueta' },
  { tipo: 'EMBALAGEM_AVARIADA', rotulo: 'Embalagem avariada', valor: 'Quantas unidades' },
  { tipo: 'VALIDADE', rotulo: 'Validade próxima ou vencida', valor: 'Validade (mês/ano)' },
  { tipo: 'CAPACIDADE_DIFERENTE', rotulo: 'Espaço diferente do alvo', valor: 'Cabem quantas' },
  { tipo: 'EAN_NAO_CADASTRADO', rotulo: 'Código de barras sem cadastro', valor: 'Código lido' },
  { tipo: 'OUTRO', rotulo: 'Outro', valor: '' },
];
export const ROTULO_DA_OCORRENCIA = Object.fromEntries(
  TIPOS_DE_OCORRENCIA.map((t) => [t.tipo, t.rotulo]),
) as Record<TipoDeOcorrencia, string>;

export interface Ocorrencia {
  readonly id: string;
  readonly tipo: TipoDeOcorrencia;
  readonly detalhe: string | null;
  readonly valor: string | null;
  readonly dia: string;
  readonly setor: string;
  readonly bloco: number;
  readonly produto: string | null;
  readonly codigo: string | null;
  readonly quem: string | null;
  readonly em: string;
  readonly resolvidoEm: string | null;
  readonly resolvidoPor: string | null;
  readonly resolucao: string | null;
}
export interface FilaDeOcorrencias {
  readonly ocorrencias: readonly Ocorrencia[];
  readonly abertas: number;
  readonly porTipo: Partial<Record<TipoDeOcorrencia, number>>;
}

export const lerOcorrencias = (situacao: 'abertas' | 'resolvidas' | 'todas' = 'abertas') =>
  ler<FilaDeOcorrencias>(`/api/giro/ocorrencias?${q({ situacao })}`);
export const registrarOcorrencia = (
  s: Sessao,
  missaoId: string,
  o: {
    readonly itemId: string | null;
    readonly tipo: TipoDeOcorrencia;
    readonly detalhe: string;
    readonly valor: string;
  },
) =>
  escrever<{ id: string }>(s, 'POST', `/api/giro/missoes/${missaoId}/ocorrencias`, {
    itemId: o.itemId,
    tipo: o.tipo,
    detalhe: o.detalhe.trim() || null,
    valor: o.valor.trim() || null,
  });
export const resolverOcorrencia = (s: Sessao, id: string, resolucao: string) =>
  escrever<{ id: string; jaEstava: boolean }>(s, 'POST', `/api/giro/ocorrencias/${id}/resolver`, {
    resolucao,
  });

/* ------------------------------------------------------------------ *
 * Pausa da missão (§13 do 1.0): separa tempo ativo de relógio corrido
 * ------------------------------------------------------------------ */

export type TipoDePausa = 'ATENDIMENTO' | 'ALMOCO' | 'INTERVALO' | 'FIM_TURNO' | 'APOIO' | 'OUTRO';
export interface PausaAberta {
  readonly id: string;
  readonly tipo: TipoDePausa;
  readonly motivo: string | null;
  readonly desde: string;
}
export const TIPOS_DE_PAUSA: readonly { readonly tipo: TipoDePausa; readonly rotulo: string }[] = [
  { tipo: 'ATENDIMENTO', rotulo: 'Atender cliente' },
  { tipo: 'ALMOCO', rotulo: 'Almoço' },
  { tipo: 'INTERVALO', rotulo: 'Intervalo' },
  { tipo: 'APOIO', rotulo: 'Ajudar no caixa / outro setor' },
  { tipo: 'FIM_TURNO', rotulo: 'Fim do turno' },
  { tipo: 'OUTRO', rotulo: 'Outro' },
];
export const pausarMissao = (s: Sessao, missaoId: string, tipo: TipoDePausa, motivo: string) =>
  escrever<{ pausaId: string; jaEstava: boolean }>(
    s,
    'POST',
    `/api/giro/missoes/${missaoId}/pausar`,
    {
      tipo,
      motivo: motivo.trim() || null,
    },
  );
export const retomarMissao = (s: Sessao, missaoId: string) =>
  escrever<{ jaEstava: boolean; pausadoMin: number }>(
    s,
    'POST',
    `/api/giro/missoes/${missaoId}/retomar`,
  );

/* ------------------------------------------------------------------ *
 * Exceção de alvo por produto (§27.6 do 1.0)
 * ------------------------------------------------------------------ */

export interface AlvoDoProduto {
  readonly id: string;
  readonly productId: string;
  readonly produto: string;
  readonly alvo: number;
  readonly motivo: string;
  readonly desde: string;
  readonly por: string | null;
}
export const lerAlvosPorProduto = () => ler<{ alvos: readonly AlvoDoProduto[] }>('/api/giro/alvos');
/** `alvo: null` tira a exceção (volta ao alvo do setor). */
export const definirAlvoDoProduto = (
  s: Sessao,
  productId: string,
  alvo: number | null,
  motivo: string,
) =>
  escrever<{ alvo: number | null; encerrou: boolean }>(s, 'PUT', `/api/giro/alvos/${productId}`, {
    alvo,
    motivo,
  });

/* ------------------------------------------------------------------ *
 * Rupturas (§38 do 1.0)
 * ------------------------------------------------------------------ */

export interface Ruptura {
  readonly productId: string | null;
  readonly codigo: string | null;
  readonly nome: string;
  readonly setor: string;
  readonly diasEmRuptura: number;
  readonly desde: string;
  readonly ate: string;
  readonly vendido: number;
  readonly ultimaConferencia: string | null;
  readonly ultimaPrateleira: number | null;
  readonly jaNaoLocalizado: boolean;
  readonly porque: string;
}
export interface RupturasDoGiro {
  readonly dia: string;
  readonly dias: number;
  readonly resumo: {
    readonly produtos: number;
    readonly cronicos: number;
    readonly nuncaConferidos: number;
    readonly sumidosDaPrateleira: number;
    readonly unidadesVendidas: number;
  };
  readonly rupturas: readonly Ruptura[];
}
export const lerRupturas = (dias: number, busca: string) =>
  ler<RupturasDoGiro>(
    `/api/giro/rupturas?${q({ dias: String(dias), busca: busca.trim() || undefined })}`,
  );

export interface ItemDaAnalise {
  readonly productId: string | null;
  readonly nome: string;
  readonly codigo: string | null;
  readonly valor: number;
}
export interface AnalisesDoGiro {
  readonly desde: string;
  readonly ate: string;
  readonly maisRepostos: readonly ItemDaAnalise[];
  readonly abaixoDoAlvo: readonly ItemDaAnalise[];
  readonly maisDivergentes: readonly ItemDaAnalise[];
  readonly rupturaRecorrente: readonly ItemDaAnalise[];
  readonly aviso: string;
}
export const lerAnalisesDoGiro = () => ler<AnalisesDoGiro>('/api/giro/analises');

/* Fotos do dia por pessoa (Carlos, 02/10) — espelho de packages/database/src/giro/fotos-do-dia.ts */
export interface FotoDoGiro {
  readonly id: string;
  readonly em: string;
  readonly por: string;
}
export interface BlocoComFotos {
  readonly missaoId: string;
  readonly setor: string;
  readonly numero: number;
  readonly estado: string;
  readonly repostos: number;
  readonly fotoDaCaixa: FotoDoGiro | null;
  readonly faltaFotoDaCaixa: boolean;
  readonly prateleira: readonly (FotoDoGiro & {
    readonly produto: string;
    readonly codigo: string | null;
  })[];
}
export interface PessoaComFotos {
  readonly accountId: string;
  readonly nome: string;
  readonly fotos: number;
  readonly faltas: number;
  readonly blocos: readonly BlocoComFotos[];
}
export interface FotosDoGiroDoDia {
  readonly dia: string;
  readonly pessoas: readonly PessoaComFotos[];
}
export const lerFotosDoDia = (dia?: string) =>
  ler<FotosDoGiroDoDia>(`/api/giro/operacao/fotos?${q({ dia })}`);

/* ------------------------------ Relatório de erros (dono 19/10) ------------------------------ */

export type SituacaoNoRelatorio =
  | 'FALHA_CONFIRMADA'
  | 'EM_ANALISE'
  | 'SEM_FALHA'
  | 'OUTRA_DIVERGENCIA'
  | 'REPOSTO'
  | 'REPOSICAO_INCOMPLETA';
export interface ItemDoRelatorio {
  readonly dia: string;
  readonly accountId: string;
  readonly balconista: string;
  readonly setor: string;
  readonly produto: string;
  readonly codigo: string | null;
  readonly prateleira: number | null;
  readonly repor: number | null;
  readonly encontrado: number | null;
  readonly reposto: number | null;
  readonly estadoItem: string;
  readonly fotoPrateleira: string | null;
  readonly fotoCesta: string | null;
  readonly divergencia: {
    readonly numero: number | null;
    readonly tipo: string;
    readonly etapa: string;
    readonly estado: string;
    readonly pedido: number;
    readonly encontrado: number;
    readonly falhaReposicao: string | null;
    readonly erroContagem: string | null;
    readonly resultado: string | null;
    readonly recontagem: number | null;
    readonly auditor: string | null;
    readonly auditadoEm: string | null;
    readonly observacao: string | null;
  } | null;
  readonly situacao: SituacaoNoRelatorio;
}
export interface BloqueioDoRelatorio {
  readonly id: string;
  readonly accountId: string;
  readonly balconista: string;
  readonly origem: string;
  readonly dia: string;
  readonly diaGiro: string | null;
  readonly erros: number | null;
  readonly mandados: number | null;
  readonly taxa: number | null;
  readonly limite: number | null;
  readonly porque: string;
  readonly motivo: string | null;
  readonly liberadoEm: string | null;
  readonly liberadoMotivo: string | null;
}
export interface RelatorioDeErrosDoGiro {
  readonly de: string;
  readonly ate: string;
  readonly resumo: QualidadeDaEquipe;
  readonly itens: readonly ItemDoRelatorio[];
  readonly bloqueios: readonly BloqueioDoRelatorio[];
  readonly conferencia: readonly ConferenciaDaPessoa[];
}
export interface ConferenciaDaPessoa {
  readonly accountId: string;
  readonly nome: string;
  readonly prateleira: number;
  readonly estoque: number;
  readonly achouNoEstoque: number;
  readonly unidadesAchadas: number;
  readonly casosFechados: number;
  readonly ajustesLancados: number;
  readonly ajustesConfirmados: number;
}
export const lerRelatorioDeErros = (de: string, ate: string, pessoa?: string | null) =>
  ler<RelatorioDeErrosDoGiro>(
    `/api/auditoria/relatorio-giro?${new URLSearchParams({
      de,
      ate,
      ...(pessoa ? { pessoa } : {}),
    }).toString()}`,
  );
